import { useAtom } from 'jotai/index';
import { atom } from 'jotai';
import { IProduct, IValueProduct } from '@shared/types.ts';
import { useCallback, useEffect } from 'react';
import { useCartService } from './use-cart-service.ts';
import { useThrottle } from '@shared/hooks/use-throttle.ts';

export const checkoutAtom = atom<{
  tab: 'pickup' | 'delivery';
  pharmacyId?: number;
  address?: string;
  build?: string;
  flat?: string;
  entrance?: string;
  comment?: string;
  name?: string;
  paymentType?: string;
}>({
  tab: 'pickup',
});
export const productsAtom = atom<IValueProduct[]>([]);

export function useCartProductsStore({ isUseCache }: { isUseCache: boolean }) {
  const [data, setData] = useAtom(productsAtom);

  const service = useCartService({
    // onUpdate: (products) => {
    //   setData(products);
    // },
  });

  useEffect(() => {
    if (service.data) {
      const products: IValueProduct[] = service.data.basket_items.map((i) => {
        return {
          ...i.product,
          value: i.quantity,
        };
      });
      setData(products);
    }
  }, [service.data]);

  const key = 'local_cart_products_list';

  const init = useCallback(() => {
    if (isUseCache) {
      const storedProducts = readCache();
      setData(storedProducts);
    }
  }, [data, isUseCache]);

  const insertProduct = useCallback(
    (product: IProduct) => {
      const isProductInCart = data.find(({ id }) => product.id === id);

      if (isProductInCart) {
        const updatedList = data.map((prevProduct) => {
          return prevProduct.id === product.id
            ? { ...product, value: prevProduct.value }
            : prevProduct;
        });
        setData(updatedList);
        if (isUseCache) updateCache(updatedList);
      } else {
        setData((state) => [...state, { ...product, value: 1 }]);
        if (isUseCache) updateCache([...data, { ...product, value: 1 }]);
      }

      if (!isUseCache) {
        service.updateProductInCart.trigger({
          productId: product.id,
          quantity: 1,
        });
      } else {
        updateCache([...data, { ...product, value: 1 }]);
      }
    },
    [data, isUseCache]
  );

  const addProducts = useCallback(
    (products: IProduct[]) => {
      setData((state) => [...state, ...toValue(products)]);

      if (isUseCache) updateCache(data);
    },
    [data, isUseCache]
  );

  const removeProductById = useCallback(
    (productId: number) => {
      const updatedList = data.filter((item) => item.id !== productId);
      setData(updatedList);

      const cartProductId = getCartProductId(productId);
      if (cartProductId && !isUseCache) {
        service.removeProductFromCart.trigger({
          cartProductId,
        });
      }
      if (isUseCache) updateCache(updatedList);
    },
    [data, isUseCache, service.data]
  );

  const clearCachedData = useCallback(() => {
    clearCache();
  }, []);

  function updateCache(products: IValueProduct[]) {
    localStorage.setItem(key, JSON.stringify(products));
  }

  function readCache(): IValueProduct[] {
    const value = localStorage.getItem(key);
    if (!value) return [];
    return JSON.parse(value);
  }

  function clearCache(): void {
    localStorage.removeItem(key);
  }

  const toValue = (products: IProduct[]): IValueProduct[] =>
    products.map((product) => ({
      ...product,
      value: 1,
    }));

  const productIds = data.map((product) => product.id);
  const isProductInCart = (productId: number) => productIds.includes(productId);

  const updateValueRemote = useThrottle<{
    productId: number;
    value: number;
  }>(({ productId, value }) => {
    service.updateProductInCart.trigger({
      productId,
      quantity: value,
    });
  });

  const updateValue = useCallback(
    (productId: number, value: number) => {
      const updatedProduct = data.map((product) => {
        return product.id === productId ? { ...product, value } : product;
      });
      setData(updatedProduct);

      if (!isUseCache) updateValueRemote({ productId, value });

      if (isUseCache) updateCache(updatedProduct);
    },
    [data, isUseCache]
  );

  const canSetValue = useCallback(
    (productId: number, value: number) => {
      const product = data.find((product) => product.id === productId);
      if (!product) return false;
      return value > 1;
    },
    [data]
  );

  const getCartProductId = (productId: number) => {
    const cartProduct = service.data?.basket_items.find(
      (product) => product.product.id === productId
    );
    return cartProduct ? cartProduct.id : null;
  };

  const validateRemove = useCallback(() => {
    const storedProducts = readCache();
    const validateList = storedProducts.map((product) => ({
      productId: product.id,
      quantity: product.value,
    }));
    if (validateList.length > 0) {
      service.updateProductsInCart.trigger(validateList);
    }
  }, []);

  return {
    data,
    init,
    insertProduct,
    addProducts,
    clearCachedData,
    removeProductById,
    productIds,
    isProductInCart,
    updateValue,
    canSetValue,
    validateRemove,
    isLoading: service.isLoading,
  };
}
